import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { changeAdminPassword, updateAdminProfile, getProfile } from '../../store/admin/admin.action';
import {
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Edit as EditIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  formContainer: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  changePasswordButton: {
    backgroundColor: '#ff9800',
    color: 'white',
    '&:hover': {
      backgroundColor: '#f57c00',
    },
  },
  saveButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  dialogContent: {
    minWidth: 400,
  },
}));

const api = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT,
});

const AdminProfilePage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { admin, token } = useSelector((state) => state.admin);

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin) {
      setProfileData({
        name: admin.name || '',
        email: admin.email || '',
      });
    }
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      await dispatch(getProfile());
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error loading profile');
    }
  };

  const handleProfileInputChange = (field) => (event) => {
    setProfileData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handlePasswordInputChange = (field) => (event) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleUpdateProfile = async () => {
    if (!profileData.name.trim() || !profileData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(updateAdminProfile(profileData));
      if (result.success) {
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New password and confirm password do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(changeAdminPassword(passwordData));
      if (result.success) {
        setPasswordDialogOpen(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset to original data
    if (admin) {
      setProfileData({
        name: admin.name || '',
        email: admin.email || '',
      });
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Admin Profile
      </Typography>

      {/* Profile Information Card */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.sectionTitle}>
            <PersonIcon className={classes.sectionIcon} />
            <Typography variant="h6">Profile Information</Typography>
            <Box style={{ marginLeft: 'auto' }}>
              {!editMode ? (
                <Tooltip title="Edit Profile">
                  <IconButton
                    color="primary"
                    onClick={() => setEditMode(true)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>

          <Box className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={profileData.name}
                  onChange={handleProfileInputChange('name')}
                  variant="outlined"
                  disabled={!editMode}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={profileData.email}
                  onChange={handleProfileInputChange('email')}
                  variant="outlined"
                  disabled={!editMode}
                  required
                  type="email"
                />
              </Grid>
            </Grid>

            {editMode && (
              <Box className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  className={classes.saveButton}
                  startIcon={<SaveIcon />}
                  onClick={handleUpdateProfile}
                  disabled={loading}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Security Settings Card */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.sectionTitle}>
            <LockIcon className={classes.sectionIcon} />
            <Typography variant="h6">Security Settings</Typography>
          </Box>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            Keep your account secure by using a strong password.
          </Typography>

          <Box className={classes.buttonContainer}>
            <Button
              variant="contained"
              className={classes.changePasswordButton}
              startIcon={<LockIcon />}
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handleClosePasswordDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={passwordData.currentPassword}
                onChange={handlePasswordInputChange('currentPassword')}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={handlePasswordInputChange('newPassword')}
                variant="outlined"
                required
                helperText="Password must be at least 6 characters long"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={handlePasswordInputChange('confirmPassword')}
                variant="outlined"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClosePasswordDialog} 
            color="secondary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleChangePassword} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminProfilePage;